import { FC, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import styled, { css } from 'styled-components'
import { Box, Flex } from '@/components/atoms/Grid'
import { MobileMenuNavigationBarTitleType } from '@/components/molecules/mobileMenuNavigationBar'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper'
import 'swiper/css'
import { makeCalcCss } from '@/utils/cssHelpers'
import { useWindowSize } from '@react-hook/window-size/throttled'
import useDisableScroll from 'hooks/useDisableScroll'
import MobileMenuNavigationBarRedesign from '@/components/molecules/mobileMenuNavigationBarRedesign'
import { FilterQueryItem } from '@/types/filterQueryItem'

const AnimationDuration = 0.2
const NavigationBarHeight = 50
const FooterHeight = 65

export type SlidePage = {
  menuWidth?: number | string
  page: JSX.Element
  navigationBartTitle?: MobileMenuNavigationBarTitleType
  filterType?: FilterQueryItem
  isResetDisabled?: boolean
}

type SlideMenuProps = {
  onDidClose?: () => void
  defaultNavigationBartTitle: MobileMenuNavigationBarTitleType
  pages: SlidePage[]
  stickyFooter?: JSX.Element
  menuPosition?: MenuPosition
  modal?: boolean
  onCancelFiltersDidTap?: () => void
  onResetPendingFilter?: (type: FilterQueryItem | string) => void
  onSlideChange?: (index: number) => void
}

export type SlideMenuActions = {
  slideNext: () => void
  slidePrev: () => void
  close: () => void
}

enum MenuPosition {
  Left,
  Right
}

interface PageItemProps {
  children: React.ReactNode
  menuHeight: number
  footerHeight: number
}

const SlideMenuRedesign = forwardRef<SlideMenuActions, SlideMenuProps>(
  (
    {
      defaultNavigationBartTitle,
      pages,
      stickyFooter,
      onDidClose,
      menuPosition,
      modal = true,
      onCancelFiltersDidTap,
      onResetPendingFilter,
      onSlideChange
    },
    ref
  ) => {
    const [show, setShow] = useState<boolean>(false)
    const [presentInDom, setPresentInDom] = useState<boolean>(false)
    const [swiper, setSwiper] = useState<SwiperClass>(null)
    const [, height] = useWindowSize()
    const [activeIndex, setActiveIndex] = useState<number>(0)

    useImperativeHandle(ref, () => ({
      close: handleClose,
      slideNext: () => {
        swiper?.slideNext()
      },
      slidePrev: () => {
        swiper?.slidePrev()
      }
    }))

    useEffect(() => {
      setPresentInDom(true)
    }, [])

    useEffect(() => {
      if (presentInDom) {
        setShow(true)
      }
    }, [presentInDom])

    const handleSlideChange = (swiperInstance: SwiperClass) => {
      const index = swiperInstance.activeIndex
      setActiveIndex(index)
      if (onSlideChange) {
        onSlideChange(index)
      }
    }

    const handleClickBack = () => {
      if (activeIndex === 0) {
        handleClose()
        return
      }
      swiper?.slidePrev()
    }

    const handleClose = () => {
      setShow(false)
      setTimeout(() => {
        setPresentInDom(false)
        onDidClose && onDidClose()
      }, AnimationDuration * 1000)
    }

    const navigationBartTitle =
      pages[activeIndex]?.navigationBartTitle || defaultNavigationBartTitle

    const currentFilterType = pages[activeIndex]?.filterType
    const currentPage = pages[activeIndex]?.page?.props
    const isResetDisabled = pages[activeIndex]?.isResetDisabled

    const footerHeight = stickyFooter ? FooterHeight : 0

    useDisableScroll(modal || show)

    return !presentInDom ? null : (
      <MobileMenuWrapper menuPosition={menuPosition}>
        <MenuItemsWrapper show={show} menuPosition={menuPosition}>
          <StyledMobileMenuNavigationBar
            title={navigationBartTitle}
            show={show}
            onClickBack={handleClickBack}
            onClickClose={handleClose}
            data-testid="mobileMenuNavigationBar"
            hideArrow={activeIndex >= 1}
            menuPosition={menuPosition}
            onCancelFiltersDidTap={onCancelFiltersDidTap}
            filterType={currentFilterType}
            page={currentPage}
            onResetPendingFilter={onResetPendingFilter}
            isActionDisabled={isResetDisabled}
          />
          <Box mt={NavigationBarHeight} pb={footerHeight}>
            <Swiper
              onInit={(swiper) => setSwiper(swiper)}
              allowTouchMove={false}
              onSlideChange={handleSlideChange}
            >
              {pages.map(({ page }, offset) => (
                <SwiperSlide key={offset}>
                  <PageItem menuHeight={height} footerHeight={footerHeight}>
                    {page}
                  </PageItem>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
          <StickyFooter show={show} menuPosition={menuPosition}>
            {stickyFooter}
          </StickyFooter>
        </MenuItemsWrapper>
        {modal && <Overlay show={show} onClick={handleClose} />}
      </MobileMenuWrapper>
    )
  }
)

export default SlideMenuRedesign

const PageItem: FC<PageItemProps> = ({
  children,
  menuHeight,
  footerHeight
}) => (
  <PageWrapper menuHeight={menuHeight} footerHeight={footerHeight}>
    <Box px={12}>{children}</Box>
  </PageWrapper>
)

const MobileMenuWrapper = styled(Flex)<{ menuPosition?: MenuPosition }>`
  position: fixed;
  ${(props) =>
    props.menuPosition === MenuPosition.Right ? 'right:0' : 'left:0'};
  top: 0;
  bottom: 0;
  background-color: transparent;
  z-index: 102;
  width: 100vw;
`

type MountedProps = {
  show: boolean
}

type MenuHeightProps = {
  menuHeight: number
  footerHeight: number
}

const MenuItemsWrapper = styled<MountedProps & { menuPosition?: MenuPosition }>(
  Box
)`
  width: 100vw;
  height: 100%;
  background-color: ${(props) => props.theme.colors.background};

  ${(props) =>
    animatePositionChange(
      props.menuPosition === MenuPosition.Right
        ? 'margin-right'
        : 'margin-left',
      props.show
    )};
`

const PageWrapper = styled<MenuHeightProps>(Box)`
  width: 100% !important;
  overflow-y: auto;
  height: ${(props) =>
    props.menuHeight
      ? `${props.menuHeight - (NavigationBarHeight + props.footerHeight)}px`
      : makeCalcCss(
          'max-height',
          `100vh - ${NavigationBarHeight + props.footerHeight}px`
        )};
`

const transitionCss = css`
  transition: ${AnimationDuration}s ease-out;
`

const animatePositionChange = (cssProperty: string, show: boolean): css =>
  css`
    ${cssProperty}: ${show ? 0 : '-100vw'};
    ${transitionCss};
  `

const StyledMobileMenuNavigationBar = styled<
  MountedProps & { menuPosition?: MenuPosition }
>(MobileMenuNavigationBarRedesign)`
  position: fixed;
  top: 0;
  width: 100%;
  height: ${NavigationBarHeight}px;

  ${(props) =>
    animatePositionChange(
      props.menuPosition === MenuPosition.Right
        ? 'margin-right'
        : 'margin-left',
      props.show
    )};
`

const Overlay = styled<MountedProps>(Box)`
  width: 100%;
  height: 100%;
  background-color: black;
  ${transitionCss};
  opacity: ${(props) => (props.show ? 0.54 : 0)};
  position: fixed;
  z-index: -1;
  left: 0;
  bottom: 0;
  top: 0;
  right: 0;
`

const StickyFooter = styled<MountedProps & { menuPosition?: MenuPosition }>(
  Box
)`
  z-index: 15;
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;

  ${(props) =>
    animatePositionChange(
      props.menuPosition === MenuPosition.Right
        ? 'margin-right'
        : 'margin-left',
      props.show
    )};
`
